import { useState, useEffect } from "react";
import {
  Box, Card, CardContent, CardHeader, Typography, FormControlLabel,
  FormGroup, Switch, Divider, Chip, Button, Avatar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Badge, alpha, createTheme, ThemeProvider, CssBaseline, RadioGroup,
  Radio, FormControl, FormLabel, Autocomplete, TextField, Stack, Alert,
  AlertTitle, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Snackbar, CircularProgress, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import {
  CreditCardOutlined, SmsOutlined, AutorenewOutlined, SettingsOutlined,
  CheckCircleOutline, CancelOutlined, InfoOutlined, NotificationsActiveOutlined,
  LockOpenOutlined, VerifiedUserOutlined, HourglassTopOutlined,
  Add as AddIcon, Delete as DeleteIcon,
  DragIndicator as DragIcon, LocalHospital as InsIcon,
  CreditCard as IdCardIcon, SaveOutlined as SaveIcon,
  PrintOutlined as PrinterIcon,
  ExpandMore as ExpandMoreIcon,
  CloudDownloadOutlined as BackupIcon,
  LanguageOutlined as DomainIcon,
  Inventory2Outlined as StockIcon,
  WorkspacePremiumOutlined as PremiumIcon,
  LocalPharmacyOutlined,
  ScienceOutlined,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import axios from "axios";
import {
  getAllInsurancePartners, addInsurancePartner,
  updateInsurancePartner, deleteInsurancePartner,
  getIdCardAmount, saveIdCardAmount,
  getCardPrinterType, saveCardPrinterType,
  complianceSettings,
  smsSettings,
  stockControlSetting,
  claimsPremiumStatus,
  claimsPremiumActivate,
  claimsPremiumRequest,
  claimsEditionPreference,
} from "../BaseURL";
import SmsSettings from "./SmsSettings";
import DomainRenewalSettings from "../Billing/DomainRenewalSettings";
import DatabaseBackupDialog from "../Billing/DatabaseBackupDialog";
import PharmacyUnpaidSettings from "./PharmacyUnpaidSettings";
import LabUnpaidSettings from "./LabUnpaidSettings";
//  Whether accounts waits for the nurse's discharge before a bill can be settled.
import BillingDischargeLockSettings from "./BillingDischargeLockSettings";
import ServiceStockSettings from "./ServiceStockSettings";

// Theme
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#1a6b8a" },
    secondary: { main: "#e07b39" },
    background: { default: "#f0f4f8", paper: "#ffffff" },
    success: { main: "#2e7d32" },
    warning: { main: "#ed6c02" },
    error: { main: "#c62828" },
  },
  typography: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    h5: { fontWeight: 700, letterSpacing: "-0.5px" },
    h6: { fontWeight: 700, fontSize: "1rem" },
    subtitle2: { fontWeight: 600 },
    caption: { fontSize: "0.72rem" },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 16px 0 rgba(26,107,138,0.08)",
          border: "1px solid #e3eaf0",
          transition: "box-shadow 0.2s",
          "&:hover": { boxShadow: "0 6px 28px 0 rgba(26,107,138,0.13)" },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { paddingBottom: 8 },
        title: { fontWeight: 700, fontSize: "0.97rem" },
        subheader: { fontSize: "0.78rem", marginTop: 2 },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    MuiSwitch: { defaultProps: { color: "primary" } },
  },
});

const PATIENTS = [
  { id: "P001", name: "Kwame Asante",  dob: "1978-03-12" },
  { id: "P002", name: "Abena Mensah",  dob: "1990-07-25" },
  { id: "P003", name: "Kofi Boateng",  dob: "1965-11-04" },
  { id: "P004", name: "Ama Owusu",     dob: "2001-01-17" },
  { id: "P005", name: "Yaw Darko",     dob: "1985-09-30" },
  { id: "P006", name: "Efua Tetteh",   dob: "1972-06-08" },
];

const CREDIT_REQUESTS = [
  { id: "REQ001", patientId: "P009", name: "Samuel Nkrumah",  type: "Outpatient", amount: "GHS 450.00",   requestedBy: "Billing Desk", date: "2025-02-24" },
  { id: "REQ002", patientId: "P017", name: "Grace Amoah",     type: "Inpatient",  amount: "GHS 1,200.00", requestedBy: "Ward 3 Nurse", date: "2025-02-25" },
  { id: "REQ003", patientId: "P031", name: "Emmanuel Ofori",  type: "Outpatient", amount: "GHS 85.00",    requestedBy: "Billing Desk", date: "2025-02-25" },
];

function IconBox({ icon, color }) {
  return (
    <Box sx={{ width: 38, height: 38, borderRadius: 2.5, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: alpha(color, 0.12), color }}>
      {icon}
    </Box>
  );
}

// Brand accent for the outer page — used for the header avatar tile.
const SETTINGS_ACCENT = "#1a6b8a";

/**
 * SettingsAccordion — uniform collapsible shell for every settings panel.
 * Wraps a legacy <Card>-based section and flattens the inner card so the
 * accordion feels like a single surface. Lets us reorganise the page into
 * grouped, expandable sections without touching each section's internals.
 */
function SettingsAccordion({ icon, color = SETTINGS_ACCENT, title, subtitle, defaultExpanded, children }) {
  return (
    <Accordion
      defaultExpanded={!!defaultExpanded}
      disableGutters
      square={false}
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        overflow: "hidden",
        "&:before": { display: "none" },
        "&.Mui-expanded": { borderColor: alpha(color, 0.5) },
        // Flatten inner Card so the accordion looks like one panel.
        "& .MuiCard-root": {
          boxShadow: "none",
          border: "none",
          bgcolor: "transparent",
          backgroundImage: "none",
        },
        // ⚠️ This hides the WHOLE CardHeader of any section rendered inside an
        // accordion — including anything passed to its `action` prop. Put
        // interactive controls (switches, buttons) in CardContent, never in
        // CardHeader, or they will silently not render. Two cards were already
        // caught by this: SMS Alert Settings and Pharmacy Stock Control.
        "& .MuiCard-root .MuiCardHeader-root": { display: "none" },
        "& .MuiCard-root .MuiDivider-root:first-of-type": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
        sx={{
          px: 2, minHeight: 64,
          "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.5, my: 1 },
          "&.Mui-expanded": { bgcolor: alpha(color, 0.08) },
          "&:hover":        { bgcolor: alpha(color, 0.04) },
        }}
      >
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.75, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: alpha(color, 0.18), color,
          border: `1px solid ${alpha(color, 0.30)}`,
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: "0.76rem", color: "text.secondary", mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1.5, md: 2 }, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

// 1. Credit & Billing
function CreditBillingSettings() {
  const [creditMode, setCreditMode] = useState("none");
  const [preApproved, setPreApproved] = useState([PATIENTS[0]]);
  const [saved, setSaved] = useState(false);
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<CreditCardOutlined fontSize="small" />} color="#1a6b8a" />}
        title="Credit & Billing Permissions"
        subheader="Control which patient bills are eligible for crediting without approval"
        action={<Chip size="small" variant="outlined"
          label={creditMode === "all" ? "All Enabled" : creditMode === "none" ? "Disabled" : "Partial"}
          color={creditMode === "all" ? "success" : creditMode === "none" ? "default" : "warning"} />}
      />
      <Divider />
      <CardContent>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ fontWeight: 700, fontSize: "0.82rem", color: "text.primary", mb: 1 }}>
            Automatic Credit Authorization
          </FormLabel>
          <RadioGroup value={creditMode} onChange={(e) => setCreditMode(e.target.value)}>
            {[
              { value: "all",        label: "All patients bills can be credited",   desc: "Any bill in the system will process credit automatically.",   color: "success.main" },
              { value: "inpatient",  label: "Only inpatient bills can be credited",  desc: "Credit applies exclusively to admitted/ward patients.",       color: "primary.main" },
              { value: "outpatient", label: "Only outpatient bills can be credited", desc: "Credit applies exclusively to OPD/clinic visits.",           color: "warning.main" },
              { value: "none",       label: "No automatic crediting",                desc: "All credit requests require manual administrative approval.", color: "error.main"   },
            ].map((opt) => (
              <Box key={opt.value} onClick={() => setCreditMode(opt.value)} sx={{
                display: "flex", alignItems: "flex-start", p: 1.2, mb: 1, borderRadius: 2,
                border: "1.5px solid", cursor: "pointer", transition: "all 0.18s",
                borderColor: creditMode === opt.value ? opt.color : "divider",
                bgcolor: creditMode === opt.value ? alpha("#1a6b8a", 0.04) : "transparent",
              }}>
                <Radio value={opt.value} size="small" sx={{ mt: -0.3, color: opt.color }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontSize: "0.85rem" }}>{opt.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
                </Box>
              </Box>
            ))}
          </RadioGroup>
        </FormControl>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.82rem", color: "text.primary" }}>
          Pre-Approved Patients{" "}
          <Typography component="span" variant="caption" color="text.secondary">
            — always credited regardless of setting above
          </Typography>
        </Typography>
        <Autocomplete multiple options={PATIENTS} getOptionLabel={(o) => `${o.name} (${o.id})`}
          value={preApproved} onChange={(_, v) => setPreApproved(v)}
          renderInput={(params) => <TextField {...params} size="small" placeholder="Search patient name or ID" />}
          renderTags={(val, getTagProps) =>
            val.map((opt, idx) => (
              <Chip key={opt.id} label={`${opt.name} - ${opt.id}`} size="small" color="primary" variant="outlined" {...getTagProps({ index: idx })} />
            ))}
          sx={{ mb: 2 }} />
        <Button variant="contained" size="small" disableElevation onClick={() => setSaved(true)} sx={{ borderRadius: 2 }}>
          Save Credit Settings
        </Button>
        <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)}
          message="Credit settings saved successfully" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      </CardContent>
    </Card>
  );
}

// 2. Credit Authorization Requests
function CreditAuthorizationRequests() {
  const [requests, setRequests] = useState(CREDIT_REQUESTS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [action, setAction] = useState("approve");
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const handleAction = (req, type) => { setSelectedReq(req); setAction(type); setDialogOpen(true); };
  const confirmAction = () => {
    setRequests((prev) => prev.filter((r) => r.id !== selectedReq.id));
    setDialogOpen(false);
    setSnack({ open: true, severity: action === "approve" ? "success" : "error",
      msg: action === "approve" ? `Credit approved for ${selectedReq.name}` : `Request declined for ${selectedReq.name}` });
  };
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<LockOpenOutlined fontSize="small" />} color="#e07b39" />}
        title="Credit Authorization Requests"
        subheader="Pending billing-side requests requiring administrative approval"
        action={<Badge badgeContent={requests.length} color="error"><HourglassTopOutlined color="action" /></Badge>}
      />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        {requests.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <VerifiedUserOutlined sx={{ fontSize: 40, color: "success.light", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No pending authorization requests</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f7fafc" }}>
                  {["Patient", "ID", "Type", "Amount", "Requested By", "Date", "Actions"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.82rem" }}>{r.name}</TableCell>
                    <TableCell><Chip label={r.patientId} size="small" variant="outlined" /></TableCell>
                    <TableCell><Chip label={r.type} size="small" variant="filled" sx={{ fontSize: "0.72rem" }} color={r.type === "Inpatient" ? "primary" : "warning"} /></TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "error.main", fontSize: "0.82rem" }}>{r.amount}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{r.requestedBy}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem", color: "text.secondary" }}>{r.date}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Approve Credit">
                          <IconButton size="small" color="success" onClick={() => handleAction(r, "approve")} sx={{ bgcolor: alpha("#2e7d32", 0.08) }}>
                            <CheckCircleOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Decline Request">
                          <IconButton size="small" color="error" onClick={() => handleAction(r, "decline")} sx={{ bgcolor: alpha("#c62828", 0.08) }}>
                            <CancelOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{action === "approve" ? "Approve Credit Request?" : "Decline Credit Request?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {action === "approve"
              ? `This will authorize the credit of ${selectedReq?.amount} for ${selectedReq?.name}. The system will process this automatically.`
              : `This will decline the credit request for ${selectedReq?.name}. The billing desk will be notified.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setDialogOpen(false)} size="small">Cancel</Button>
          <Button variant="contained" size="small" disableElevation onClick={confirmAction}
            sx={{ borderRadius: 2 }} color={action === "approve" ? "success" : "error"}>
            {action === "approve" ? "Confirm Approval" : "Confirm Decline"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </Card>
  );
}

// 3. SMS Settings
// The master ON/OFF toggle is the only setting on this card that is
// persisted server-side (into system_settings.sms.enabled — same source
// SmsService reads at send time). The per-event switches below are UI
// placeholders — the corresponding automatic triggers don't yet exist
// in the backend (only the welcome-package one is wired via
// PatientInformation.js and always runs when the master toggle is ON
// and an API key is configured). The full provider config (API key,
// sender ID, endpoints, balance check, test send) lives on the
// dedicated "SMS Settings" page.
function SmsAlertSettings() {
  const [masterSms, setMasterSms] = useState(true);
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [savingMaster,  setSavingMaster]  = useState(false);
  const [options, setOptions] = useState({ welcomePackage: true, onApproval: true, onBillGenerated: false, onDischarge: true, onAppointment: false });
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const toggle = (key) => setOptions((o) => ({ ...o, [key]: !o[key] }));

  // Load the persisted master flag on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(smsSettings);
        if (!cancelled && res.data?.success) {
          setMasterSms(!!res.data.data.enabled);
        }
      } catch (e) {
        console.error('SMS settings load failed:', e.message);
      } finally {
        if (!cancelled) setLoadingMaster(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onMasterChange = async (e) => {
    const next = e.target.checked;
    setMasterSms(next);   // optimistic
    setSavingMaster(true);
    try {
      // Only send `enabled` — leave the rest of the SMS config (api_key,
      // sender_id, paths) untouched by omitting them. The PUT handler
      // preserves any field it doesn't receive.
      const res = await axios.put(smsSettings, { enabled: next });
      if (!res.data?.success) throw new Error(res.data?.message || 'Save failed');
      setSnack({ open: true, msg: `SMS gateway ${next ? 'enabled' : 'disabled'}`, severity: 'success' });
    } catch (err) {
      setMasterSms(!next);  // roll back
      setSnack({ open: true, msg: `Failed to save: ${err.message}`, severity: 'error' });
    } finally {
      setSavingMaster(false);
    }
  };

  const smsOptions = [
    { key: "welcomePackage",  label: "Welcome package SMS",       desc: "Sent to new patients upon registration (wired)" },
    { key: "onApproval",      label: "Credit approval SMS",        desc: "Notify patient when credit is approved (not wired yet)" },
    { key: "onBillGenerated", label: "Bill generated SMS",         desc: "Alert patient when a new bill is raised (not wired yet)" },
    { key: "onDischarge",     label: "Discharge notification SMS", desc: "Send summary on patient discharge (not wired yet)" },
    { key: "onAppointment",   label: "Appointment reminder SMS",   desc: "Remind patients of upcoming appointments (sent immediately, not scheduled)" },
  ];
  // The master switch used to live in `CardHeader action`, where
  // SettingsAccordion's `.MuiCardHeader-root { display: none }` made it
  // invisible — so SMS could not be turned on or off from this page at all.
  // Moved into CardContent, same as Pharmacy Stock Control.
  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 2, p: 2, mb: 2, borderRadius: 2,
            border: '1px solid', borderColor: masterSms ? 'success.light' : 'divider',
            bgcolor: (t) => alpha(masterSms ? t.palette.success.main : t.palette.text.disabled, 0.07),
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
              {loadingMaster ? 'Loading…' : (masterSms ? 'SMS is ON' : 'SMS is OFF')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Master gateway switch. Provider credentials live on the SMS Settings page.
            </Typography>
          </Box>
          <Switch
            checked={masterSms}
            onChange={onMasterChange}
            disabled={loadingMaster || savingMaster}
            color="success"
          />
        </Box>

        {!masterSms && !loadingMaster && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <AlertTitle sx={{ fontSize: "0.82rem", fontWeight: 700 }}>SMS globally disabled</AlertTitle>
            No SMS will be sent regardless of individual settings below.
          </Alert>
        )}
        <FormGroup>
          {smsOptions.map((opt) => (
            <Box key={opt.key} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              py: 1.2, px: 1.5, mb: 0.8, borderRadius: 2, transition: "all 0.18s",
              bgcolor: options[opt.key] && masterSms ? alpha("#7b3f9e", 0.05) : "#f8f8f8",
              border: "1px solid", borderColor: options[opt.key] && masterSms ? alpha("#7b3f9e", 0.2) : "divider",
              opacity: masterSms ? 1 : 0.5,
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontSize: "0.84rem" }}>{opt.label}</Typography>
                <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
              </Box>
              <Switch checked={options[opt.key]} onChange={() => toggle(opt.key)} size="small" disabled={!masterSms} />
            </Box>
          ))}
        </FormGroup>
        <Snackbar
          open={snack.open}
          autoHideDuration={2500}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// 3b. Pharmacy stock control — master on/off
//
// Some facilities do not run a stocked pharmacy: they assume every drug is
// always on hand and do not want the software counting anything. Turning this
// OFF makes prescribing and billing ignore dispensary stock completely — no
// availability check, no soft reservation, no "insufficient stock" dialog in
// the consulting room, and no out-of-stock rejection at the cash desk.
//
// Leaving it ON is the existing behaviour, so untouched installs are unchanged.
function PharmacyStockSettings() {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [snack, setSnack]     = useState({ open: false, msg: '', severity: 'success' });

  useEffect(() => {
    let cancelled = false;
    axios.get(stockControlSetting)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) setEnabled(res.data.enabled !== false);
      })
      .catch(() => { /* leave the safe default (on) */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const onChange = async (value) => {
    const next = value === 'enforce';
    if (next === enabled) return;
    setEnabled(next);            // optimistic
    setSaving(true);
    try {
      const res = await axios.put(stockControlSetting, { enabled: next });
      if (!res.data?.success) throw new Error(res.data?.message || 'Save failed');
      setSnack({
        open: true,
        severity: 'success',
        msg: next
          ? 'Stock is enforced — a drug with no recorded stock cannot be prescribed'
          : 'Stock is no longer enforced — anything can be prescribed, and stock is still recorded',
      });
    } catch (err) {
      setEnabled(!next);         // roll back so the screen never lies
      setSnack({ open: true, severity: 'error', msg: err.message || 'Could not save' });
    } finally {
      setSaving(false);
    }
  };

  /* Worded by consequence, not by mechanism. Nobody administering a hospital
   * should have to know what a soft reservation against a balance table is to
   * choose correctly here. */
  const OPTIONS = [
    {
      value: 'enforce',
      label: 'Enforce stock',
      desc: 'A drug with no recorded stock cannot be prescribed. This is the default.',
      color: 'success.main',
    },
    {
      value: 'off',
      label: 'Do not enforce — prescribe anything, any time',
      desc:
        'Stock is still recorded and still reported; it simply never refuses a ' +
        'prescription. A balance may go negative, which is an honest record of ' +
        'dispensing something the system did not know it held. For a facility ' +
        'that does not keep counted stock, or whose balances were never loaded.',
      color: 'warning.main',
    },
  ];
  const mode = enabled ? 'enforce' : 'off';

  // NOTE: no <CardHeader> here. SettingsAccordion hides every nested
  // CardHeader ("& .MuiCard-root .MuiCardHeader-root": { display: "none" })
  // so it can show one title instead of two — anything put in a CardHeader,
  // including an `action` switch, is invisible. The control has to live in
  // CardContent.
  return (
    <Card>
      <CardContent>
        <FormControl component="fieldset" fullWidth disabled={loading || saving}>
          <FormLabel
            component="legend"
            sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'text.primary', mb: 1 }}
          >
            {loading ? 'Loading…' : 'When a drug has no recorded stock'}
          </FormLabel>
          <RadioGroup value={mode} onChange={(e) => onChange(e.target.value)}>
            {OPTIONS.map((opt) => (
              <Box
                key={opt.value}
                onClick={() => { if (!loading && !saving) onChange(opt.value); }}
                sx={{
                  display: 'flex', alignItems: 'flex-start', p: 1.2, mb: 1, borderRadius: 2,
                  border: '1.5px solid', cursor: 'pointer', transition: 'all 0.18s',
                  borderColor: mode === opt.value ? opt.color : 'divider',
                  bgcolor: mode === opt.value ? alpha('#6d28d9', 0.04) : 'transparent',
                }}
              >
                <Radio value={opt.value} size="small" sx={{ mt: -0.3, color: opt.color }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>
                    {opt.label}
                    {opt.value === 'enforce' && (
                      <Chip
                        size="small"
                        label="Default"
                        sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
                </Box>
              </Box>
            ))}
          </RadioGroup>
        </FormControl>

        <Alert severity={enabled ? 'info' : 'warning'} sx={{ borderRadius: 2, mt: 1 }}>
          {enabled ? (
            <Typography variant="body2" component="div">
              A drug that has never been stocked in the dispensary cannot be
              prescribed at all, and a prescription is refused once the recorded
              stock runs out.
              <Box component="span" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Choose the second option if this facility does not keep counted
                stock — doctors can then prescribe anything, any time.
              </Box>
            </Typography>
          ) : (
            <Typography variant="body2" component="div">
              Nothing is refused for stock reasons. Stock is <b>still recorded</b> —
              movements are written, low-stock alerts and procurement keep working,
              and a balance may go negative where more was dispensed than the system
              knew it held.
              <Box component="span" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                A negative balance is deliberate: it makes the shortfall visible in
                the stock reports instead of hiding it behind a refused
                prescription. Switching back takes effect within 30 seconds.
              </Box>
            </Typography>
          )}
        </Alert>
      </CardContent>
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Card>
  );
}

// 3c. Claims interface — standard (free) vs premium (licensed add-on)
//
// Two independent things decide which Claims screen opens:
//   ENTITLEMENT — held in the CENTRAL database by TotalCare. Perpetual: once
//                 granted it never expires. Read-only from here.
//   SELECTION   — this facility's own choice, saved locally. Only settable
//                 once entitled.
//
// Note the control lives in CardContent, not CardHeader — SettingsAccordion
// hides nested CardHeaders, which would make the whole thing invisible.
function ClaimsInterfaceSettings() {
  const [info, setInfo]         = useState(null);   // null while loading
  const [code, setCode]         = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [activating, setActivating] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [switching, setSwitching]   = useState(false);
  const [snack, setSnack]       = useState({ open: false, msg: '', severity: 'success' });

  const load = () => {
    axios.get(claimsPremiumStatus)
      .then((r) => { if (r.data?.success) setInfo(r.data); })
      .catch(() => setInfo({ entitled: false, licensed: false, preference: 'default', hospital: '—' }));
  };

  useEffect(load, []);

  const onActivate = async () => {
    if (!code.trim()) return;
    setActivating(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const res = await axios.post(claimsPremiumActivate, {
        code: code.trim(),
        currentUser: userInfo.username || userInfo.name || 'Unknown',
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Activation failed');
      setSnack({ open: true, severity: 'success', msg: res.data.message });
      setCode('');
      load();
    } catch (err) {
      setSnack({
        open: true, severity: 'error',
        msg: err.response?.data?.message || err.message || 'Activation failed',
      });
    } finally {
      setActivating(false);
    }
  };

  // Asking for the licence. The facility's only route to being licensed — it
  // writes a request into the central database and nothing else. It cannot grant
  // anything, by construction rather than by a permission check.
  const onRequest = async () => {
    setRequesting(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const res = await axios.post(claimsPremiumRequest, {
        currentUser: userInfo.username || userInfo.name || 'Unknown',
        note: requestNote.trim() || undefined,
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Could not send the request');
      setSnack({ open: true, severity: 'success', msg: res.data.message });
      setRequestNote('');
      load();
    } catch (err) {
      setSnack({
        open: true, severity: 'error',
        msg: err.response?.data?.message || err.message || 'Could not send the request',
      });
    } finally {
      setRequesting(false);
    }
  };

  const onSelect = async (edition) => {
    setSwitching(true);
    try {
      const res = await axios.put(claimsEditionPreference, { edition });
      if (!res.data?.success) throw new Error(res.data?.message || 'Could not save');
      setInfo((p) => ({ ...p, preference: edition }));
      setSnack({
        open: true, severity: 'success',
        msg: edition === 'premium'
          ? 'Premium Claims selected. Reopen Claims to see it.'
          : 'Standard Claims selected. Reopen Claims to see it.',
      });
    } catch (err) {
      setSnack({
        open: true, severity: 'error',
        msg: err.response?.data?.message || err.message || 'Could not save',
      });
    } finally {
      setSwitching(false);
    }
  };

  const entitled = !!info?.entitled;
  const premiumSelected = info?.preference === 'premium';

  // Five distinct states, each needing a different thing said and a different
  // action offered. Collapsing them into licensed/not-licensed is what makes a
  // card like this confusing: "expired" and "never asked" look identical to the
  // user but need opposite next steps.
  const expired   = !!info?.expired;                            // was licensed, term ran out
  const pending   = info?.requestStatus === 'pending' && !entitled;
  const declined  = info?.requestStatus === 'declined' && !entitled;
  const daysLeft  = info?.daysLeft;
  const expiringSoon = entitled && typeof daysLeft === 'number' && daysLeft <= 30;
  const canRequest = info !== null && !entitled && !pending && info?.requestsSupported !== false;

  const statusLabel =
    info === null ? '…'
    : entitled    ? (expiringSoon ? `${daysLeft}d LEFT` : 'LICENSED')
    : expired     ? 'EXPIRED'
    : pending     ? 'REQUESTED'
    : declined    ? 'DECLINED'
    : 'NOT LICENSED';

  const statusColour =
    info === null ? 'default'
    : entitled    ? (expiringSoon ? 'warning' : 'success')
    : expired     ? 'error'
    : pending     ? 'info'
    : 'default';

  const headline =
    info === null ? 'Checking licence…'
    : entitled    ? 'Premium Claims is licensed'
    : expired     ? 'Premium Claims has expired'
    : pending     ? 'Licence requested — awaiting TotalCare'
    : declined    ? 'Licence request was declined'
    : 'Premium Claims is not licensed';

  const fmt = (d) => (d ? String(d).slice(0, 10) : null);

  return (
    <Card>
      <CardContent>
        {/* Licence state — read-only, owned by TotalCare */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 2, p: 2, mb: 2, borderRadius: 2,
            border: '1px solid', borderColor: entitled ? 'success.light' : 'divider',
            bgcolor: (t) => alpha(entitled ? t.palette.success.main : t.palette.text.disabled, 0.07),
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
              {headline}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              Facility: <strong>{info?.hospital || '—'}</strong>
              {/* The term, when there is one. A date alone is not enough — "expires
                  2027-08-23" makes the reader do the arithmetic, so the days are
                  spelled out beside it. */}
              {entitled && info?.expires_at
                ? ` · expires ${fmt(info.expires_at)}${typeof daysLeft === 'number' ? ` (${daysLeft} days)` : ''}`
                : ''}
              {entitled && !info?.expires_at ? ' · no expiry' : ''}
              {expired && info?.expires_at ? ` · expired ${fmt(info.expires_at)}` : ''}
              {pending && info?.requested_at
                ? ` · asked ${fmt(info.requested_at)}${info.requested_by ? ` by ${info.requested_by}` : ''}`
                : ''}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={statusLabel}
            color={statusColour}
            variant={entitled || pending ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }}
          />
        </Box>

        {/* Renewal warning. Fires inside the last 30 days, while there is still
            time to act — a notice that appears on the day of expiry is a
            postmortem, not a warning. */}
        {expiringSoon && (
          <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
            <Typography variant="body2">
              This licence expires in <strong>{daysLeft} day{daysLeft === 1 ? '' : 's'}</strong>
              {info?.expires_at ? ` (${fmt(info.expires_at)})` : ''}. Request a renewal
              below to keep the premium interface. If it lapses, Claims returns to
              the standard screen — no claims work is lost.
            </Typography>
          </Alert>
        )}

        {declined && info?.declineReason && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
            <Typography variant="body2">{info.declineReason}</Typography>
          </Alert>
        )}

        {/* ── Ask for the licence ──────────────────────────────────────────
            The primary route, and the only one that needs nothing from anybody
            first. Shown whenever the facility is not currently entitled and has
            no request outstanding — so it doubles as the RENEWAL button once a
            term has lapsed, which is why the wording is chosen from state
            rather than fixed. */}
        {canRequest && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {expired
                ? 'This facility’s licence has run out. Request a renewal and TotalCare will review it.'
                : declined
                  ? 'You can ask again once the reason above has been resolved.'
                  : 'Ask TotalCare for a premium Claims licence. Licences run for one year and can be renewed.'}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small" fullWidth
                placeholder="Anything TotalCare should know (optional)"
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                disabled={requesting}
                onKeyDown={(e) => { if (e.key === 'Enter') onRequest(); }}
              />
              <Button
                variant="contained" onClick={onRequest}
                disabled={requesting}
                startIcon={requesting ? <CircularProgress size={15} sx={{ color: 'white' }} /> : <PremiumIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', px: 3 }}
              >
                {requesting ? 'Sending…' : expired ? 'Request renewal' : 'Request licence'}
              </Button>
            </Stack>
          </Box>
        )}

        {/* Waiting. No action to offer, so none is shown — but asking again is
            allowed, because a facility chasing a request expects to be able to,
            and it refreshes the timestamp in TotalCare's queue. */}
        {pending && (
          <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
            <Typography variant="body2">
              Your request has been sent to TotalCare and is waiting to be
              reviewed. Nothing further is needed here — this card updates on its
              own once the licence is granted.
            </Typography>
            <Button
              size="small" onClick={onRequest} disabled={requesting}
              sx={{ textTransform: 'none', mt: 0.5, px: 0 }}
            >
              {requesting ? 'Sending…' : 'Send a reminder'}
            </Button>
          </Alert>
        )}

        {/* ── Activation code ──────────────────────────────────────────────
            The secondary route, for when TotalCare issues a code instead of
            granting directly. Kept behind the request block because most
            facilities will never be given one. */}
        {info !== null && !entitled && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Or, if TotalCare has given you an activation code, enter it here.
              The one-year term starts from the day you activate.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small" fullWidth
                placeholder="e.g. TC-PREM-7K4Q-9XR2"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={activating}
                onKeyDown={(e) => { if (e.key === 'Enter') onActivate(); }}
              />
              <Button
                variant="outlined" onClick={onActivate}
                disabled={activating || !code.trim()}
                startIcon={activating ? <CircularProgress size={15} /> : <PremiumIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', px: 3 }}
              >
                {activating ? 'Activating…' : 'Activate'}
              </Button>
            </Stack>
          </Box>
        )}

        {/* Which screen to work in */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Claims screen in use</Typography>
        <RadioGroup
          value={premiumSelected ? 'premium' : 'default'}
          onChange={(e) => onSelect(e.target.value)}
        >
          <FormControlLabel
            value="default" disabled={switching} control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>Standard Claims</Typography>
                <Typography variant="caption" color="text.secondary">
                  The Claims screen included with the system
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="premium" disabled={switching || !entitled} control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Premium Claims{!entitled && ' — requires a licence'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The redesigned Claims interface with its own form and rules
                </Typography>
              </Box>
            }
          />
        </RadioGroup>

        <Alert severity="info" sx={{ borderRadius: 2, mt: 1.5 }}>
          <Typography variant="body2">
            The choice applies the next time Claims is opened. If the licence
            expires or is withdrawn, Claims returns to the standard screen
            automatically — no one is locked out of claims work, and nothing is
            lost. Renewing brings the premium screen straight back.
          </Typography>
        </Alert>
      </CardContent>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Card>
  );
}

// 4. Auto-Update
function AutoUpdateSettings() {
  const [opts, setOpts] = useState({ masterAutoUpdate: true, billsAutoRefresh: true, stockAutoSync: false, labResultsAutoLoad: true, reportsAutoGenerate: false });
  const [saved, setSaved] = useState(false);
  const toggle = (k) => setOpts((o) => ({ ...o, [k]: !o[k] }));
  const items = [
    { key: "billsAutoRefresh",    label: "Bills auto-refresh",        desc: "Automatically refresh billing lists in real-time" },
    { key: "stockAutoSync",       label: "Stock/pharmacy auto-sync",   desc: "Sync pharmacy and store stock levels automatically" },
    { key: "labResultsAutoLoad",  label: "Lab results auto-load",      desc: "Push lab results to patient records automatically" },
    { key: "reportsAutoGenerate", label: "Reports auto-generate",      desc: "Auto-generate daily/weekly reports on schedule" },
  ];
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<AutorenewOutlined fontSize="small" />} color="#2e8b57" />}
        title="Auto-Update Settings"
        subheader="Control system-wide automatic data refresh and sync behaviour"
        action={
          <FormControlLabel labelPlacement="start"
            control={<Switch checked={opts.masterAutoUpdate} onChange={() => toggle("masterAutoUpdate")} size="small" />}
            label={<Typography variant="caption" fontWeight={700} color={opts.masterAutoUpdate ? "success.main" : "text.disabled"}>{opts.masterAutoUpdate ? "AUTO ON" : "AUTO OFF"}</Typography>}
          />}
      />
      <Divider />
      <CardContent>
        <FormGroup>
          {items.map((item) => (
            <Box key={item.key} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              py: 1.2, px: 1.5, mb: 0.8, borderRadius: 2, transition: "all 0.18s",
              bgcolor: opts[item.key] && opts.masterAutoUpdate ? alpha("#2e8b57", 0.05) : "#f8f8f8",
              border: "1px solid", borderColor: opts[item.key] && opts.masterAutoUpdate ? alpha("#2e8b57", 0.2) : "divider",
              opacity: opts.masterAutoUpdate ? 1 : 0.5,
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontSize: "0.84rem" }}>{item.label}</Typography>
                <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
              </Box>
              <Switch checked={opts[item.key]} onChange={() => toggle(item.key)} size="small" disabled={!opts.masterAutoUpdate} />
            </Box>
          ))}
        </FormGroup>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" size="small" disableElevation onClick={() => setSaved(true)}
            sx={{ borderRadius: 2, bgcolor: "#2e8b57", "&:hover": { bgcolor: "#1e6b40" } }}>
            Save Auto-Update Settings
          </Button>
        </Box>
        <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)} message="Auto-update settings saved" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      </CardContent>
    </Card>
  );
}

// 5. Notifications
function NotificationSettings() {
  const [opts, setOpts] = useState({ dashboardAlerts: true, emailSummary: false, criticalAlerts: true, auditLog: true });
  const [saved, setSaved] = useState(false);
  const toggle = (k) => setOpts((o) => ({ ...o, [k]: !o[k] }));
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<NotificationsActiveOutlined fontSize="small" />} color="#c0392b" />}
        title="Notification & Alert Preferences"
        subheader="System notifications for administrators and authorised staff"
      />
      <Divider />
      <CardContent>
        <FormGroup>
          {[
            { key: "dashboardAlerts", label: "Dashboard alert banners", desc: "Show system alerts on the admin dashboard" },
            { key: "emailSummary",    label: "Daily email summary",      desc: "Send a daily digest email to the admin account" },
            { key: "criticalAlerts",  label: "Critical event alerts",    desc: "Immediate alerts for critical billing or system events" },
            { key: "auditLog",        label: "Audit log recording",      desc: "Log all administrative actions for compliance review" },
          ].map((item) => (
            <Box key={item.key} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              py: 1.2, px: 1.5, mb: 0.8, borderRadius: 2, transition: "all 0.18s",
              bgcolor: opts[item.key] ? alpha("#c0392b", 0.04) : "#f8f8f8",
              border: "1px solid", borderColor: opts[item.key] ? alpha("#c0392b", 0.18) : "divider",
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontSize: "0.84rem" }}>{item.label}</Typography>
                <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
              </Box>
              <Switch checked={opts[item.key]} onChange={() => toggle(item.key)} size="small" />
            </Box>
          ))}
        </FormGroup>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" size="small" disableElevation onClick={() => setSaved(true)}
            sx={{ borderRadius: 2, bgcolor: "#c0392b", "&:hover": { bgcolor: "#922b21" } }}>
            Save Notification Settings
          </Button>
        </Box>
        <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)} message="Notification settings saved" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      </CardContent>
    </Card>
  );
}

// 6. Insurance Partners (integrated from InsurancePartnerSettings.jsx)
function InsurancePartnerSettings() {
  const [partners, setPartners] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [newName,  setNewName]  = useState("");
  const [newShort, setNewShort] = useState("");
  const [adding,   setAdding]   = useState(false);

  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const username = user?.username || user?.name || "admin";

  const loadPartners = () => {
    setLoading(true);
    fetch(getAllInsurancePartners)
      .then((r) => r.json())
      .then((data) => { if (data?.success) setPartners(data.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPartners(); }, []);

  const activeCount = partners.filter((p) => p.is_active).length;

  const handleAdd = () => {
    if (!newName.trim()) return;
    setAdding(true);
    fetch(addInsurancePartner, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName.trim(), short_name: newShort.trim() || null, is_active: 1, sort_order: partners.length, created_by: username }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success) { setNewName(""); setNewShort(""); loadPartners(); }
        else Swal.fire("Error", data.message || "Failed to add", "error");
      })
      .catch(() => Swal.fire("Error", "Network error", "error"))
      .finally(() => setAdding(false));
  };

  const handleToggle = (partner) => {
    const newActive = partner.is_active ? 0 : 1;
    if (newActive === 1 && activeCount >= 10) {
      Swal.fire("Limit Reached", "Only 10 partners can be active on the card at once. Deactivate one first.", "warning");
      return;
    }
    fetch(updateInsurancePartner, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...partner, is_active: newActive }),
    })
      .then((r) => r.json())
      .then((data) => { if (data?.success) loadPartners(); else Swal.fire("Error", data.message, "error"); })
      .catch(() => {});
  };

  const handleDelete = (partner) => {
    Swal.fire({ title: "Delete partner?", text: `Remove "${partner.name}" from the list?`, icon: "warning",
      showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Yes, delete" })
      .then((result) => {
        if (!result.isConfirmed) return;
        fetch(deleteInsurancePartner, {
          method: "DELETE", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: partner.id }),
        })
          .then((r) => r.json())
          .then((data) => { if (data?.success) loadPartners(); else Swal.fire("Error", data.message, "error"); })
          .catch(() => {});
      });
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<InsIcon fontSize="small" />} color="#1a6b8a" />}
        title="Insurance Partners"
        subheader="Manage partners displayed on the patient ID card (up to 10 active)"
        action={
          <Chip size="small" variant="outlined"
            label={`${activeCount} / 10 active`}
            color={activeCount >= 10 ? "warning" : "success"} />
        }
      />
      <Divider />
      <CardContent>
        <Box sx={{ display: "flex", gap: 1, mb: 2.5, alignItems: "flex-start" }}>
          <TextField size="small" label="Partner name" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            sx={{ flex: 2 }} />
          <TextField size="small" label="Short name" value={newShort}
            onChange={(e) => setNewShort(e.target.value)}
            sx={{ flex: 1 }} inputProps={{ maxLength: 10 }} />
          <Button variant="contained" size="small" disableElevation onClick={handleAdd}
            disabled={adding || !newName.trim()}
            startIcon={adding ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
            sx={{ height: 40, whiteSpace: "nowrap", borderRadius: 2 }}>
            Add
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : partners.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center", fontSize: "0.85rem" }}>
            No insurance partners yet. Add one above.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
            {partners.map((p) => (
              <Box key={p.id} sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                px: 1.5, py: 1, borderRadius: 2, transition: "all 0.2s",
                background: p.is_active ? alpha("#1a6b8a", 0.04) : "#fafafa",
                border: "1px solid", borderColor: p.is_active ? alpha("#1a6b8a", 0.15) : "divider",
                opacity: p.is_active ? 1 : 0.6,
              }}>
                <DragIcon sx={{ color: "#ccc", fontSize: 18, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} fontSize={13} noWrap>{p.name}</Typography>
                  {p.short_name && <Typography variant="caption" color="text.secondary">{p.short_name}</Typography>}
                </Box>
                <Chip size="small" variant="outlined" sx={{ fontSize: "0.7rem", mr: 0.5 }}
                  label={p.is_active ? "Active" : "Hidden"}
                  color={p.is_active ? "success" : "default"} />
                <Tooltip title={p.is_active ? "Deactivate" : "Activate"}>
                  <Switch size="small" checked={Boolean(p.is_active)} onChange={() => handleToggle(p)} />
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(p)} sx={{ color: "error.light" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
          Toggle the switch to show or hide a partner on the ID card. Only the first 10 active partners are printed.
        </Typography>
      </CardContent>
    </Card>
  );
}


// 7. ID Card Amount Setup
function IdCardAmountSettings() {
  const [amount,  setAmount]  = useState("");
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const username = user?.username || user?.name || "admin";

  useEffect(() => {
    fetch(getIdCardAmount)
      .then((r) => r.json())
      .then((data) => {
        if (data?.success && data?.data?.amount != null)
          setAmount(String(data.data.amount));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = () => {
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed < 0) {
      setError("Please enter a valid amount (0 or greater).");
      return;
    }
    setError("");
    setSaving(true);
    fetch(saveIdCardAmount, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount: parsed, updated_by: username }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success) setSaved(true);
        else setError(data?.message || "Failed to save.");
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setSaving(false));
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<IdCardIcon fontSize="small" />} color="#0277bd" />}
        title="ID Card Amount Setup"
        subheader="Set the fee charged to patients for printing an ID card"
        action={
          <Chip size="small" variant="outlined" color="info"
            label={loading ? "Loading..." : amount !== "" ? ("GHS " + parseFloat(amount || 0).toFixed(2)) : "Not set"} />
        }
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              This amount is charged whenever a patient ID card is printed or re-printed.
              Set to <strong>0</strong> to make ID cards free of charge.
            </Typography>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="ID Card Fee"
                type="number"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                size="small"
                sx={{ width: 220 }}
                inputProps={{ min: 0, step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" fontWeight={700} color="text.secondary">GHS</Typography>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(error)}
                helperText={error || " "}
              />
              <Button
                variant="contained" size="small" disableElevation
                onClick={handleSave}
                disabled={saving || amount === ""}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                sx={{ height: 40, borderRadius: 2, bgcolor: "#0277bd", "&:hover": { bgcolor: "#01579b" } }}
              >
                {saving ? "Saving..." : "Save Amount"}
              </Button>
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                Quick presets:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {["0", "2.00", "5.00", "10.00", "20.00", "50.00"].map((preset) => (
                  <Chip
                    key={preset}
                    label={preset === "0" ? "Free" : ("GHS " + preset)}
                    size="small"
                    variant={amount === preset ? "filled" : "outlined"}
                    color={amount === preset ? "info" : "default"}
                    onClick={() => { setAmount(preset); setError(""); }}
                    sx={{ cursor: "pointer", fontWeight: 600, fontSize: "0.72rem" }}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </CardContent>

      <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          ID card fee updated successfully
        </Alert>
      </Snackbar>
    </Card>
  );
}

// 7b. ID Card Printer Type
// Facilities standardised on either the Magicard 300 or the Zebra ZXP
// Series 3C — the two printers use different card-print pipelines and
// slightly different safe-margins, so the OPD print button reads this
// setting at print time and picks the matching HTML layout.
function CardPrinterSettings() {
  const PRINTERS = [
    {
      value: "magicard_300",
      label: "Magicard 300",
      desc: "Direct-to-card, YMCKO ribbon. Uses the full CR80 print area (85.6 × 54 mm bleed).",
    },
    {
      value: "zebra_zxp_3c",
      label: "Zebra ZXP Series 3C",
      desc: "YMCKO print, 1 mm no-print zone at the card edges. Layout is inset for edge-safe printing.",
    },
  ];

  const [printer, setPrinter] = useState("magicard_300");
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const username = user?.username || user?.name || "admin";

  useEffect(() => {
    fetch(getCardPrinterType)
      .then((r) => r.json())
      .then((data) => {
        const t = data?.data?.printer_type;
        if (t && PRINTERS.some((p) => p.value === t)) setPrinter(t);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    setError("");
    setSaving(true);
    fetch(saveCardPrinterType, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ printer_type: printer, updated_by: username }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success) setSaved(true);
        else setError(data?.message || "Failed to save.");
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setSaving(false));
  };

  const currentLabel = PRINTERS.find((p) => p.value === printer)?.label || "—";

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<PrinterIcon fontSize="small" />} color="#00695c" />}
        title="ID Card Printer"
        subheader="Pick the physical printer this facility uses so the OPD print job matches its layout"
        action={
          <Chip size="small" variant="outlined" color="success"
            label={loading ? "Loading…" : currentLabel} />
        }
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The OPD "Print ID Card" button will automatically use this printer's layout.
              Switching printers does not require the front-office to change anything else.
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={printer} onChange={(e) => setPrinter(e.target.value)}>
                {PRINTERS.map((p) => (
                  <FormControlLabel
                    key={p.value}
                    value={p.value}
                    control={<Radio />}
                    sx={{
                      alignItems: "flex-start", mb: 1, mr: 0,
                      "& .MuiFormControlLabel-label": { width: "100%" },
                    }}
                    label={
                      <Box sx={{ pt: 0.25 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                          {p.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.desc}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {error && (
              <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}>{error}</Alert>
            )}

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained" size="small" disableElevation
                onClick={handleSave} disabled={saving}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                sx={{ height: 40, borderRadius: 2, bgcolor: "#00695c",
                      "&:hover": { bgcolor: "#004d40" } }}
              >
                {saving ? "Saving…" : "Save Printer"}
              </Button>
            </Box>
          </>
        )}
      </CardContent>

      <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          Card printer preference updated
        </Alert>
      </Snackbar>
    </Card>
  );
}

// 8. General placeholder
function GeneralSettings() {
  return (
    <Card sx={{ borderStyle: "dashed", bgcolor: "#fafcfe" }}>
      <CardHeader
        avatar={<IconBox icon={<SettingsOutlined fontSize="small" />} color="#888" />}
        title="Additional Settings"
        subheader="Reserved for future configuration modules"
      />
      <Divider />
      <CardContent>
        <Box sx={{ minHeight: 120, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "text.disabled", gap: 1 }}>
          <InfoOutlined sx={{ fontSize: 36 }} />
          <Typography variant="body2">More settings will appear here</Typography>
          <Typography variant="caption">Contact your system administrator to configure additional modules.</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ROOT
// Compliance — Vitals & Nursing Notes per-day rule
function ComplianceSettings() {
  const [startDate, setStartDate]   = useState("");
  const [draftDate, setDraftDate]   = useState("");
  const [minPerDay, setMinPerDay]   = useState(3);
  const [enabled, setEnabled]       = useState("on");      // server value
  const [draftEnabled, setDraftEnabled] = useState("on");  // toggle in UI
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [pwdOpen, setPwdOpen]       = useState(false);
  const [pwdMode, setPwdMode]       = useState("date");    // 'date' | 'toggle'
  const [username, setUsername]     = useState("");
  const [password, setPassword]     = useState("");
  const [error, setError]           = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(complianceSettings);
      if (data?.success) {
        setStartDate(data.startDate || "");
        setDraftDate(data.startDate || "");
        setMinPerDay(data.minPerDay || 3);
        const en = (data.enabled || "on") === "off" ? "off" : "on";
        setEnabled(en);
        setDraftEnabled(en);
      }
    } catch (err) {
      console.error("Failed to load compliance settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSaveClick = () => {
    setError("");
    if (!draftDate) { setError("Please pick a start date."); return; }
    if (draftDate === startDate) { setError("No changes to save."); return; }
    setPwdMode("date");
    setUsername("");
    setPassword("");
    setPwdOpen(true);
  };

  // Triggered when the admin flips the Switch. Confirms with password before
  // sending the change — same admin gate the start-date update already uses.
  const onToggleEnabled = (e) => {
    const next = e.target.checked ? "on" : "off";
    setDraftEnabled(next);
    setError("");
    setPwdMode("toggle");
    setUsername("");
    setPassword("");
    setPwdOpen(true);
  };

  const submitUpdate = async () => {
    if (!username.trim() || !password) {
      setError("Username and password are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        username: username.trim(),
        password,
      };
      if (pwdMode === "date") payload.startDate = draftDate;
      else                    payload.enabled   = draftEnabled;

      const { data } = await axios.post(complianceSettings, payload);
      if (data?.success) {
        setStartDate(data.startDate || "");
        setDraftDate(data.startDate || "");
        const en = (data.enabled || "on") === "off" ? "off" : "on";
        setEnabled(en);
        setDraftEnabled(en);
        setPwdOpen(false);
        Swal.fire({
          icon: "success",
          title: pwdMode === "toggle"
            ? (en === "on" ? "Compliance rule turned ON" : "Compliance rule turned OFF")
            : "Compliance start date updated",
          text: pwdMode === "toggle"
            ? (en === "on"
                ? "Discharge will now be blocked when the per-day rule is not met."
                : "Discharge can now proceed without meeting the per-day rule.")
            : `Effective from ${data.startDate}.`,
          timer: 2400,
          showConfirmButton: false,
        });
      } else {
        setError(data?.message || "Update failed.");
        if (pwdMode === "toggle") setDraftEnabled(enabled); // rollback
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message || "Update failed.";
      if (status === 401) setError("Incorrect username or password.");
      else if (status === 403) setError("Only a System Administrator can update this setting.");
      else setError(msg);
      if (pwdMode === "toggle") setDraftEnabled(enabled); // rollback
    } finally {
      setSaving(false);
    }
  };

  const cancelPwd = () => {
    setPwdOpen(false);
    setError("");
    if (pwdMode === "toggle") setDraftEnabled(enabled); // rollback the Switch
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<HourglassTopOutlined />} color="#b45309" />}
        title="Admission Compliance"
        subheader={`Minimum ${minPerDay} vitals & ${minPerDay} nursing notes per admission day`}
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Admissions whose admission date is <strong>before</strong> the start date below are exempt
          and will not block discharge. Pick a future or current date to phase the rule in cleanly.
        </Typography>

        <Stack spacing={1.25}>
          {/* Master on/off — when OFF, the discharge gate is skipped entirely */}
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              p: 1.25, borderRadius: 1.5,
              border: "1px solid",
              borderColor: draftEnabled === "on" ? "rgba(46,125,50,0.35)" : "rgba(198,40,40,0.35)",
              bgcolor:     draftEnabled === "on" ? "rgba(46,125,50,0.06)" : "rgba(198,40,40,0.06)",
            }}
          >
            <Box sx={{ minWidth: 0, pr: 1 }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "text.primary" }}>
                Enforce per-day vitals &amp; nursing notes
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {draftEnabled === "on"
                  ? `ON — discharge is blocked until each admission day has ≥${minPerDay} vitals and ≥1 nursing note.`
                  : "OFF — staff can discharge a patient regardless of per-day vitals/notes."}
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Switch
                  checked={draftEnabled === "on"}
                  onChange={onToggleEnabled}
                  disabled={loading || saving}
                />
              }
              label=""
            />
          </Box>

          <TextField
            label="Compliance Start Date"
            type="date"
            value={draftDate}
            onChange={(e) => setDraftDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={loading || enabled === "off"}
            size="small"
            fullWidth
            helperText={enabled === "off"
              ? "Start date is ignored while the rule is OFF."
              : " "}
          />
          {startDate && (
            <Typography variant="caption" color="text.secondary">
              Currently effective from <strong>{startDate}</strong>.
            </Typography>
          )}
          {error && !pwdOpen && (
            <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>{error}</Alert>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 0.5 }}>
            <Button
              size="small"
              onClick={() => { setDraftDate(startDate); setError(""); }}
              disabled={loading || saving || draftDate === startDate}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={onSaveClick}
              disabled={loading || saving || draftDate === startDate}
            >
              Update
            </Button>
          </Box>
        </Stack>
      </CardContent>

      <Dialog open={pwdOpen} onClose={() => !saving && cancelPwd()} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Confirm — System Administrator</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5, fontSize: '0.85rem' }}>
            {pwdMode === "toggle"
              ? <>You are about to turn the per-day vitals/notes rule <strong>{draftEnabled === "on" ? "ON" : "OFF"}</strong>. Enter your admin credentials to apply.</>
              : <>New start date: <strong>{draftDate}</strong>. Enter your admin credentials to apply.</>}
          </DialogContentText>
          <Stack spacing={1.25}>
            <TextField
              label="Username"
              size="small"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            {error && (
              <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>{error}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={cancelPwd} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submitUpdate}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// Database Backup — admin-gated one-click download of the entire DB as a
// mysqldump, plain .sql by default or gzipped on request (a 2 GB DB arrives
// as roughly 200 MB .sql.gz). Password confirmation reuses the same
// admin-credentials pattern the Compliance settings use.
function DatabaseBackupSettings() {
  const [open, setOpen] = useState(false);

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<BackupIcon fontSize="small" />} color="#0369a1" />}
        title="Database Backup"
        subheader="Download a full snapshot of the hospital database as an .sql file"
      />
      <Divider />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Generates a live snapshot of every table (patients, bills, labs, medications,
          audit logs — everything) and downloads it as an SQL file (or compressed, if
          you choose). Use the file with any MySQL/MariaDB tool to restore or migrate.
        </Typography>
        <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <AlertTitle sx={{ fontSize: "0.82rem", fontWeight: 700 }}>What to expect</AlertTitle>
          Small databases finish in a few seconds. A 1–2 GB database can take
          several minutes to stream. Keep this tab open until the file save
          dialog appears in your browser.
        </Alert>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<BackupIcon />}
            onClick={() => setOpen(true)}
            sx={{ height: 40, borderRadius: 2, bgcolor: "#0369a1", "&:hover": { bgcolor: "#075985" } }}
          >
            Download Database
          </Button>
        </Box>
      </CardContent>

      {/* Shared with the lapsed-subscription screen — see Billing/DatabaseBackupDialog. */}
      <DatabaseBackupDialog open={open} onClose={() => setOpen(false)} />
    </Card>
  );
}

export default function HospitalAdminSettings() {
  // Sections grouped by area. Each item becomes a collapsible accordion;
  // add a new section by dropping a row into the right group. Keeping the
  // wiring here in one place means individual section components stay
  // untouched — they just render inside the accordion body.
  const SECTIONS = [
    {
      group: "Billing & Authorization",
      items: [
        { id: "credit",       icon: <CreditCardOutlined  fontSize="small" />, color: "#1a6b8a", title: "Credit & Billing Permissions",  subtitle: "Control which patient bills are eligible for crediting without approval", component: <CreditBillingSettings />,        defaultExpanded: true },
        { id: "auth",         icon: <LockOpenOutlined    fontSize="small" />, color: "#e07b39", title: "Credit Authorization Requests", subtitle: "Pending billing-side requests requiring administrative approval",         component: <CreditAuthorizationRequests /> },
        { id: "idcard",       icon: <IdCardIcon          fontSize="small" />, color: "#0277bd", title: "ID Card Amount Setup",          subtitle: "Set the fee charged to patients for printing an ID card",                 component: <IdCardAmountSettings /> },
        { id: "pharmunpaid",  icon: <LocalPharmacyOutlined fontSize="small" />, color: "#7b3f9e", title: "Unpaid Prescription Visibility", subtitle: "Whether the dispensary can see, and dispense, prescriptions before payment", component: <PharmacyUnpaidSettings /> },
        // Titles below spell out PAYMENT vs STOCK. The two pairs of cards were
        // named "Unpaid Lab & Scan Visibility" and "Lab & Scan Stock Control"
        // and sat in different sections of this page — near-identical names,
        // far apart, so the stock card got changed when payment was meant.
        // Both now say which gate they are, and each points at the other.
        { id: "labunpaid",    icon: <ScienceOutlined       fontSize="small" />, color: "#1a6b8a", title: "Lab & Scan — PAYMENT gate (unpaid orders)",  subtitle: "Whether the laboratory can see, and run, orders before payment. NOT the reagent/film setting — that is “Lab & Scan — STOCK gate” under Clinical & System.", component: <LabUnpaidSettings /> },
        // Sits with the billing cards rather than with the ward ones on purpose:
        // it changes what ACCOUNTS may do, and the ward's own behaviour is
        // identical whichever way it is set.
        { id: "dischargelock", icon: <LockOpenOutlined     fontSize="small" />, color: "#b45309", title: "Discharge before billing", subtitle: "Whether accounts must wait for a nurse to complete the discharge before a bill can be settled", component: <BillingDischargeLockSettings /> },
      ],
    },
    {
      group: "Printing & Cards",
      items: [
        { id: "cardprinter",  icon: <PrinterIcon         fontSize="small" />, color: "#00695c", title: "ID Card Printer",               subtitle: "Pick the physical printer this facility uses so the OPD print job matches its layout", component: <CardPrinterSettings /> },
        { id: "insurance",    icon: <InsIcon             fontSize="small" />, color: "#1a6b8a", title: "Insurance Partners",            subtitle: "Manage partners displayed on the patient ID card (up to 10 active)",       component: <InsurancePartnerSettings /> },
      ],
    },
    {
      group: "Communications",
      items: [
        { id: "sms",          icon: <SmsOutlined                     fontSize="small" />, color: "#7b3f9e", title: "SMS Alert Settings",              subtitle: "Master on/off + per-event triggers",                            component: <SmsAlertSettings /> },
        { id: "smsgateway",   icon: <SmsOutlined                     fontSize="small" />, color: "#11998e", title: "SMS Gateway Configuration",       subtitle: "SMS Online GH provider — API key, sender ID, balance, test send", component: <SmsSettings /> },
        { id: "notifications",icon: <NotificationsActiveOutlined     fontSize="small" />, color: "#c0392b", title: "Notification & Alert Preferences", subtitle: "System notifications for administrators and authorised staff", component: <NotificationSettings /> },
      ],
    },
    {
      group: "Clinical & System",
      items: [
        { id: "compliance",   icon: <HourglassTopOutlined fontSize="small" />, color: "#b45309", title: "Admission Compliance",          subtitle: "Per-day vitals and nursing-note rules that gate patient discharge", component: <ComplianceSettings /> },
        { id: "stockcontrol", icon: <StockIcon            fontSize="small" />, color: "#6d28d9", title: "Pharmacy Stock Control",        subtitle: "Whether a drug with no recorded stock can still be prescribed", component: <PharmacyStockSettings /> },
        { id: "labstock",     icon: <ScienceOutlined      fontSize="small" />, color: "#0e7490", title: "Lab & Scan — STOCK gate (reagents & film)",      subtitle: "Whether a test can be ordered once its reagents or film have run out. This has NOTHING to do with payment — for that use “Lab & Scan — PAYMENT gate” under Billing & Authorization.", component: <ServiceStockSettings /> },
        { id: "claimsui",     icon: <PremiumIcon          fontSize="small" />, color: "#7c3aed", title: "Claims Interface",              subtitle: "Choose the standard or the premium Claims screen, and activate a licence", component: <ClaimsInterfaceSettings /> },
        { id: "autoupdate",   icon: <AutorenewOutlined    fontSize="small" />, color: "#2e8b57", title: "Auto-Update Settings",          subtitle: "Control system-wide automatic data refresh and sync behaviour",     component: <AutoUpdateSettings /> },
        { id: "general",      icon: <SettingsOutlined     fontSize="small" />, color: "#64748b", title: "Additional Settings",           subtitle: "Reserved for future configuration modules",                          component: <GeneralSettings /> },
      ],
    },
    {
      group: "Data Management",
      items: [
        { id: "backup",       icon: <BackupIcon           fontSize="small" />, color: "#0369a1", title: "Database Backup",               subtitle: "Download a full snapshot of the hospital database as an .sql file", component: <DatabaseBackupSettings /> },
      ],
    },
    {
      group: "Licence & Hosting",
      items: [
        { id: "domain",       icon: <DomainIcon           fontSize="small" />, color: "#0f766e", title: "Domain & Hosting Renewal",      subtitle: "Renewal invoice, expiry date and payment status for this facility's domain", component: <DomainRenewalSettings /> },
      ],
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: { xs: 2, md: 3 } }}>
        {/* Page header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
          <Avatar sx={{
            bgcolor: alpha(SETTINGS_ACCENT, 0.18),
            color: SETTINGS_ACCENT,
            border: `1px solid ${alpha(SETTINGS_ACCENT, 0.30)}`,
            width: 44, height: 44,
          }}>
            <SettingsOutlined />
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: "text.primary", lineHeight: 1.15 }}>
              System Settings
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "text.secondary", mt: 0.25 }}>
              Administrative configuration — click any panel to expand its options.
            </Typography>
          </Box>
        </Stack>

        {/* Grouped, collapsible sections */}
        <Stack spacing={3} sx={{ mt: 3 }}>
          {SECTIONS.map((g) => (
            <Box key={g.group}>
              <Typography sx={{
                fontSize: "0.7rem", fontWeight: 800, letterSpacing: "1.2px",
                textTransform: "uppercase", color: "text.secondary",
                mb: 1, pl: 0.5,
              }}>
                {g.group}
              </Typography>
              {/* Two panels per row instead of one tall column — the page was
                  long enough that reaching the lower groups meant scrolling
                  past everything above them.

                  EVERY panel is half width. There are no full-width exceptions:
                  an occasional panel spanning both columns broke the alignment
                  down the page and read as a mistake rather than as emphasis.
                  The one panel with a table (Credit Authorization Requests)
                  scrolls that table inside its own card instead — TableContainer
                  already applies overflow-x, and minWidth: 0 below is what lets
                  the column actually shrink to its share so it can.

                  minmax(0, 1fr) rather than a bare 1fr: a bare 1fr takes its
                  minimum from the content, so that table would otherwise push
                  its column past half and shove the row sideways.

                  alignItems: start stops a collapsed panel being stretched to
                  match an expanded one beside it — grid items fill their row
                  height by default, which would leave a tall empty card. */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.25,
                  alignItems: "start",
                }}
              >
                {g.items.map((s) => (
                  <Box key={s.id} sx={{ minWidth: 0 }}>
                    <SettingsAccordion
                      icon={s.icon}
                      color={s.color}
                      title={s.title}
                      subtitle={s.subtitle}
                      defaultExpanded={s.defaultExpanded}
                    >
                      {s.component}
                    </SettingsAccordion>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </ThemeProvider>
  );
}
